import { useEffect, useState, useCallback, useRef } from 'react';
import { ClientApi, ToastStateHandler } from '../../api';
import { useOrganizationId } from '../state';
import { useContainer } from '../useContainer';
import { useTranslation } from '../useTranslation';
import { InvoiceDto } from '../../commonTypes/invoice.types';
import { InvoiceStatus } from '@cyberlabrs/helpers';

interface BaseQuery {
    page?: number;
    size?: number;
    clientId?: string;
    statuses?: InvoiceStatus[];
    search?: string;
    sort?: 'createdAt' | 'clientName' | 'number';
    direction?: 'asc' | 'desc';
}

interface HookOptions extends BaseQuery {
    mode?: 'pagination' | 'infinite';
}

export const useHttpGetInvoices = (options: HookOptions = {}) => {
    const { t } = useTranslation('newNotifications');
    const organizationId = useOrganizationId();
    const clientApi: ClientApi = useContainer(ClientApi);
    const toastHandler = useContainer(ToastStateHandler);

    const [data, setData] = useState<InvoiceDto[]>([]);
    const [currentPage, setCurrentPage] = useState(options.page ?? 0);
    const [isLoading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(true);
    const [total, setTotal] = useState<number | null>(null);

    const isFetching = useRef(false);

    const { mode, ...queryOptions } = options;

    const fetchInvoices = useCallback(
        (append = false) => {
            if (!organizationId || isFetching.current) return;
            isFetching.current = true;
            setLoading(true);

            const { exec } = clientApi.getInvoices({
                params: { organizationId },
                query: { ...queryOptions, page: options.page || currentPage, size: options.size ?? 10 },
                onSuccess: res => {
                    const items = res.body.items;
                    setData(prev => (append ? [...prev, ...items] : items));
                    if (res.body.total != null) {
                        setTotal(res.body.total);
                        setHasMore((append ? data.length : 0) + items.length < res.body.total);
                    } else {
                        setHasMore(items.length === (options.size ?? 10));
                    }
                    isFetching.current = false;
                    setLoading(false);
                },
                onError: () => {
                    toastHandler.push('error', t('common.error'), t('common.somethingWentWrong'));
                    isFetching.current = false;
                    setLoading(false);
                },
            });

            exec();
        },
        [organizationId, clientApi, toastHandler, t, queryOptions, options.size]
    );

    useEffect(() => {
        if (!organizationId) return;
        fetchInvoices(currentPage > 0);
    }, [organizationId, currentPage]);

    const loadMore = useCallback(() => {
        if (mode !== 'infinite' || isFetching.current || !hasMore) return;
        setCurrentPage(prev => prev + 1);
    }, [hasMore, mode]);

    const setPage = useCallback(
        (page: number) => {
            if (mode !== 'pagination') return;
            setCurrentPage(page);
            fetchInvoices(false);
        },
        [fetchInvoices, mode]
    );

    const refetch = useCallback(() => {
        const targetPage = currentPage > 0 && data.length === 1 ? currentPage - 1 : currentPage;

        setData([]);
        setHasMore(true);
        setCurrentPage(targetPage);
        fetchInvoices(false);
    }, [currentPage, data.length, fetchInvoices]);

    return {
        data,
        isLoading,
        hasMore,
        total,
        currentPage,
        loadMore: mode === 'infinite' ? loadMore : undefined,
        setPage: mode === 'pagination' ? setPage : undefined,
        refetch,
    };
};

export default useHttpGetInvoices;
